import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { ChatbotMessage, FAQItem } from '../types';
import * as chatbotService from '../services/chatbotService';

interface ChatbotContextType {
  isOpen: boolean;
  messages: ChatbotMessage[];
  isLoading: boolean;
  openChatbot: () => void;
  closeChatbot: () => void;
  toggleChatbot: () => void;
  sendMessage: (message: string) => Promise<void>;
  faqData: FAQItem[];
}

const ChatbotContext = createContext<ChatbotContextType | undefined>(undefined);

export const useChatbot = () => {
  const context = useContext(ChatbotContext);
  if (!context) {
    throw new Error('useChatbot must be used within a ChatbotProvider');
  }
  return context;
};

export const ChatbotProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatbotMessage[]>([
    { text: 'Hi, I am here to help. Ask me about Olitask setup, payments, helpers, safety, or downloads.', isUser: false, timestamp: new Date() }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [faqData, setFaqData] = useState<FAQItem[]>([]);

  useEffect(() => {
    chatbotService.loadFaq().then(setFaqData);
  }, []);

  const openChatbot = useCallback(() => setIsOpen(true), []);
  const closeChatbot = useCallback(() => setIsOpen(false), []);
  const toggleChatbot = useCallback(() => setIsOpen(prev => !prev), []);

  const sendMessage = useCallback(async (question: string) => {
    if (!question.trim()) return;

    setMessages(prev => [...prev, { text: question, isUser: true, timestamp: new Date() }]);
    setIsLoading(true);

    try {
      const answer = await chatbotService.getAnswer(question, faqData);
      setMessages(prev => [...prev, { text: answer, isUser: false, timestamp: new Date() }]);
    } catch (error) {
      setMessages(prev => [...prev, { 
        text: "I'm having trouble connecting. Please try again later.", 
        isUser: false, 
        timestamp: new Date() 
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [faqData]);

  return (
    <ChatbotContext.Provider
      value={{
        isOpen,
        messages,
        isLoading,
        openChatbot,
        closeChatbot,
        toggleChatbot,
        sendMessage,
        faqData,
      }}
    >
      {children}
    </ChatbotContext.Provider>
  );
};